import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, CornerDownRight, Recycle, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { ApiError, api, fieldErrors, unwrap, type Schemas } from '../../api/client';
import type { Asset, AssetTreeNode } from '../../api/types';
import { AssetLink } from '../../components/assets/AssetLink';
import { AssetPicker, LocationSelect } from '../../components/pickers';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  DateInput,
  EmptyState,
  ErrorBox,
  Field,
  FormGrid,
  Input,
  Loading,
  Notice,
  nullIfEmpty,
  PageHeader,
  Select,
  StatusBadge,
  Textarea,
  useToast,
} from '../../components/ui';
import { CHILD_ACTION_LABELS, METHOD_LABELS, todayIso } from '../../lib/format';

type ChildAction = 'dispose' | 'remove_to_stock' | 'move_to_other_asset' | 'keep_as_independent_asset';
type Decision = { action: ChildAction | ''; target_location: string; target_asset: Asset | null };
type Node = AssetTreeNode & { level: number; parentId: string };

const emptyDecision = (): Decision => ({ action: '', target_location: '', target_asset: null });

/**
 * Verschrottung mit Entscheidung für jede eingebaute Komponente.
 * Entscheidungen braucht jedes Child eines verschrotteten Geräts – wird ein Child mitverschrottet,
 * brauchen auch dessen Children eine Entscheidung (wie im Backend).
 */
export function DisposalWizardPage() {
  const { ref } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  const initial = useQuery({
    queryKey: ['asset', ref],
    queryFn: () => unwrap(api.GET('/api/assets/{ref}', { params: { path: { ref: ref! } } })),
    enabled: !!ref,
  });

  const [primaries, setPrimaries] = useState<Asset[]>([]);
  useEffect(() => {
    if (initial.data && primaries.length === 0) setPrimaries([initial.data]);
  }, [initial.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const trees = useQueries({
    queries: primaries.map((p) => ({
      queryKey: ['asset', p.id, 'children', 'recursive'],
      queryFn: () => unwrap(api.GET('/api/assets/{ref}/children', { params: { path: { ref: p.id }, query: { recursive: 'true' } } })),
    })),
  });

  // Baum je Haupt-Asset in Anzeige-Reihenfolge
  const nodesByPrimary = useMemo(() => {
    return primaries.map((p, i) => {
      const flat = trees[i]?.data?.data ?? [];
      const byParent = new Map<string, AssetTreeNode[]>();
      for (const n of flat) {
        const pid = n.parent?.id ?? p.id;
        byParent.set(pid, [...(byParent.get(pid) ?? []), n]);
      }
      const out: Node[] = [];
      const walk = (pid: string, level: number) => {
        for (const n of (byParent.get(pid) ?? []).sort((a, b) => a.asset_number.localeCompare(b.asset_number))) {
          out.push({ ...n, level, parentId: pid });
          walk(n.id, level + 1);
        }
      };
      walk(p.id, 0);
      return out;
    });
  }, [primaries, trees]);

  const [decisions, setDecisions] = useState<Record<string, Decision>>({});
  const [defaultLocation, setDefaultLocation] = useState('');
  const [header, setHeader] = useState({
    disposal_date: todayIso(),
    reason: '',
    condition: '',
    method: 'certified_data_destruction' as Schemas['CreateDisposal']['method'],
    performed_by_name: '',
    disposal_company: '',
    certificate_number: '',
    notes: '',
    generate_protocol: true,
  });

  const decisionOf = (id: string) => decisions[id] ?? emptyDecision();
  const setDecision = (id: string, d: Partial<Decision>) => setDecisions((all) => ({ ...all, [id]: { ...(all[id] ?? emptyDecision()), ...d } }));

  // Welche Knoten brauchen eine Entscheidung? Direkte Children der Haupt-Assets und Children mitverschrotteter Komponenten.
  const primaryIds = new Set(primaries.map((p) => p.id));
  const visibleNodes = nodesByPrimary.map((nodes) => {
    const disposedIds = new Set(primaryIds);
    const visible: Node[] = [];
    for (const n of nodes) {
      if (!disposedIds.has(n.parentId)) continue;
      visible.push(n);
      if (decisionOf(n.id).action === 'dispose') disposedIds.add(n.id);
    }
    return visible;
  });
  const allVisible = visibleNodes.flat();
  const disposedCount = primaries.length + allVisible.filter((n) => decisionOf(n.id).action === 'dispose').length;
  const openDecisions = allVisible.filter((n) => {
    const d = decisionOf(n.id);
    if (!d.action) return true;
    if (d.action === 'move_to_other_asset' && !d.target_asset) return true;
    if (d.action === 'remove_to_stock' && !d.target_location && !defaultLocation) return true;
    return false;
  });

  const setAll = (action: ChildAction) => setDecisions((all) => Object.fromEntries(allVisible.map((n) => [n.id, { ...(all[n.id] ?? emptyDecision()), action }])));

  const submit = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/api/disposals', {
          body: {
            assets: primaries.map((p) => p.id),
            disposal_date: header.disposal_date || undefined,
            reason: header.reason.trim(),
            condition: nullIfEmpty(header.condition),
            method: header.method,
            performed_by_name: nullIfEmpty(header.performed_by_name),
            disposal_company: nullIfEmpty(header.disposal_company),
            certificate_number: nullIfEmpty(header.certificate_number),
            notes: nullIfEmpty(header.notes),
            default_target_location: defaultLocation || undefined,
            generate_protocol: header.generate_protocol,
            children: allVisible.map((n) => {
              const d = decisionOf(n.id);
              return {
                asset: n.id,
                action: d.action as ChildAction,
                target_asset: d.action === 'move_to_other_asset' ? d.target_asset?.id : undefined,
                target_location: (d.action === 'remove_to_stock' || d.action === 'keep_as_independent_asset') && d.target_location ? d.target_location : undefined,
              };
            }),
          },
        }),
      ),
    onSuccess: (d) => {
      void qc.invalidateQueries();
      toast(`Verschrottung ${d.disposal_number} erfasst`);
      for (const w of d.warnings ?? []) toast(w, 'error');
      navigate(`/disposals/${d.disposal_number}`);
    },
  });

  const errs = fieldErrors(submit.error);
  const childErrors = useMemo(() => {
    const out: Record<string, string> = {};
    for (const [path, msg] of Object.entries(errs)) {
      const m = /^children\.(\d+)/.exec(path);
      const node = m ? allVisible[Number(m[1])] : undefined;
      if (node) out[node.id] = msg;
    }
    return out;
  }, [errs, allVisible]);

  if (ref && initial.isPending) return <Loading />;
  if (ref && initial.error) return <ErrorBox error={initial.error} />;

  const loadingTrees = trees.some((t) => t.isPending);
  const canSubmit = primaries.length > 0 && header.reason.trim() && !loadingTrees && openDecisions.length === 0;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) submit.mutate();
      }}
    >
      <PageHeader
        back={
          <Link to={ref ? `/assets/${ref}` : '/disposals'} className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-800">
            <ArrowLeft className="size-4" /> Zurück
          </Link>
        }
        title="Verschrotten"
        subtitle="Assets werden nicht gelöscht, sondern mit Protokoll als verschrottet markiert. Das lässt sich nicht rückgängig machen."
      />

      <div className="space-y-5">
        <Card>
          <CardHeader title="1. Was wird verschrottet?" description="Ein Protokoll je Hauptgerät; mitverschrottete Komponenten erscheinen darin unter „Gegenstand“." />
          <CardBody className="space-y-3">
            {primaries.length === 0 && <EmptyState title="Noch kein Gerät ausgewählt" />}
            {primaries.map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-2 rounded-md border border-slate-200 px-3 py-2">
                <div className="text-sm">
                  <AssetLink asset={p} /> <span className="font-medium">{p.name}</span>
                  <span className="ml-2 text-slate-500">
                    {p.asset_type.label}
                    {p.serial_number ? ` · SN ${p.serial_number}` : ''}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={p.status} />
                  <Button size="sm" variant="ghost" aria-label="Entfernen" icon={<X className="size-4" />} onClick={() => setPrimaries((ps) => ps.filter((x) => x.id !== p.id))} />
                </div>
              </div>
            ))}
            <Field label="Weiteres Gerät hinzufügen">
              {(pr) => (
                <AssetPicker
                  id={pr.id}
                  value={null}
                  exclude={primaries.map((p) => p.id)}
                  onChange={(a) => a && setPrimaries((ps) => [...ps, a])}
                  placeholder="Inventarnummer oder Seriennummer …"
                />
              )}
            </Field>
            {primaries.some((p) => p.parent) && (
              <Notice tone="warning">Ein ausgewähltes Gerät ist eingebaut. Es wird beim Verschrotten automatisch aus seinem Gerät ausgebaut.</Notice>
            )}
          </CardBody>
        </Card>

        {primaries.length > 0 && (
          <Card>
            <CardHeader
              title="2. Eingebaute Komponenten"
              description="Für jede eingebaute Komponente festlegen, was mit ihr passiert."
              actions={
                allVisible.length > 0 && (
                  <>
                    <Button size="sm" onClick={() => setAll('dispose')}>
                      Alle mitverschrotten
                    </Button>
                    <Button size="sm" onClick={() => setAll('remove_to_stock')}>
                      Alle ins Lager
                    </Button>
                  </>
                )
              }
            />
            <CardBody className="space-y-4">
              {loadingTrees ? (
                <Loading label="Komponenten werden geladen …" />
              ) : allVisible.length === 0 ? (
                <p className="text-sm text-slate-500">Keine Komponenten mit Inventarnummer eingebaut. Komponenten ohne Nummer werden automatisch mit ausgebaut.</p>
              ) : (
                <>
                  <Field label="Standard-Ziel für ausgebaute Teile" hint="Gilt für „Ausbauen → Lager“, wenn dort kein Standort gewählt ist.">
                    {(p) => <LocationSelect {...p} value={defaultLocation} onChange={setDefaultLocation} emptyLabel="– bitte wählen –" />}
                  </Field>
                  {primaries.map((p, i) =>
                    (visibleNodes[i] ?? []).length === 0 ? null : (
                      <div key={p.id}>
                        <div className="mb-2 text-xs font-semibold text-slate-500 uppercase">
                          In {p.asset_number} {p.name}
                        </div>
                        <div className="space-y-2">
                          {(visibleNodes[i] ?? []).map((n) => (
                            <ChildDecisionRow
                              key={n.id}
                              node={n}
                              decision={decisionOf(n.id)}
                              onChange={(d) => setDecision(n.id, d)}
                              exclude={[...primaryIds, n.id]}
                              error={childErrors[n.id]}
                            />
                          ))}
                        </div>
                      </div>
                    ),
                  )}
                </>
              )}
            </CardBody>
          </Card>
        )}

        <Card>
          <CardHeader title="3. Angaben für das Protokoll" />
          <CardBody>
            <FormGrid className="lg:grid-cols-3">
              <Field label="Verschrottungsdatum" error={errs.disposal_date}>
                {(p) => <DateInput {...p} max={todayIso()} value={header.disposal_date} onChange={(e) => setHeader((h) => ({ ...h, disposal_date: e.target.value }))} />}
              </Field>
              <Field label="Art der Entsorgung" required>
                {(p) => (
                  <Select {...p} value={header.method} onChange={(e) => setHeader((h) => ({ ...h, method: e.target.value as typeof h.method }))}>
                    {Object.entries(METHOD_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>
                        {v}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field label="Zustand" hint="z. B. defekt, veraltet">
                {(p) => <Input {...p} value={header.condition} onChange={(e) => setHeader((h) => ({ ...h, condition: e.target.value }))} />}
              </Field>
              <Field label="Grund" required error={errs.reason} className="sm:col-span-2 lg:col-span-3">
                {(p) => <Textarea {...p} rows={2} value={header.reason} onChange={(e) => setHeader((h) => ({ ...h, reason: e.target.value }))} placeholder="z. B. Mainboard defekt, Reparatur unwirtschaftlich" />}
              </Field>
              <Field label="Durchgeführt von">
                {(p) => <Input {...p} value={header.performed_by_name} onChange={(e) => setHeader((h) => ({ ...h, performed_by_name: e.target.value }))} />}
              </Field>
              <Field label="Entsorgungsunternehmen">
                {(p) => <Input {...p} value={header.disposal_company} onChange={(e) => setHeader((h) => ({ ...h, disposal_company: e.target.value }))} />}
              </Field>
              <Field label="Nachweis-Nr." hint="Kann später ergänzt werden">
                {(p) => <Input {...p} value={header.certificate_number} onChange={(e) => setHeader((h) => ({ ...h, certificate_number: e.target.value }))} />}
              </Field>
              <Field label="Bemerkungen" className="sm:col-span-2 lg:col-span-3">
                {(p) => <Textarea {...p} rows={2} value={header.notes} onChange={(e) => setHeader((h) => ({ ...h, notes: e.target.value }))} />}
              </Field>
            </FormGrid>
            <Checkbox
              className="mt-4"
              label="Verschrottungsprotokoll (PDF) direkt erzeugen und am Gerät speichern"
              checked={header.generate_protocol}
              onChange={(e) => setHeader((h) => ({ ...h, generate_protocol: e.target.checked }))}
            />
          </CardBody>
        </Card>

        {submit.error instanceof ApiError && submit.error.code === 'CHILD_DECISION_REQUIRED' ? (
          <ErrorBox error={new Error('Inzwischen wurden weitere Komponenten eingebaut. Bitte Seite neu laden und für alle Komponenten entscheiden.')} />
        ) : (
          <ErrorBox error={submit.error} />
        )}

        <div className="flex flex-wrap items-center justify-end gap-3">
          {openDecisions.length > 0 && <span className="text-sm text-amber-700">Noch {openDecisions.length} Komponente(n) ohne vollständige Entscheidung</span>}
          <Button onClick={() => navigate(-1)}>Abbrechen</Button>
          <Button type="submit" variant="danger" icon={<Recycle className="size-4" />} loading={submit.isPending} disabled={!canSubmit}>
            {disposedCount} Asset{disposedCount === 1 ? '' : 's'} verschrotten
          </Button>
        </div>
      </div>
    </form>
  );
}

function ChildDecisionRow({
  node,
  decision,
  onChange,
  exclude,
  error,
}: {
  node: Node;
  decision: Decision;
  onChange: (d: Partial<Decision>) => void;
  exclude: string[];
  error?: string;
}) {
  return (
    <div className={`rounded-md border px-3 py-2 ${error ? 'border-red-300 bg-red-50' : 'border-slate-200'}`} style={{ marginLeft: node.level * 24 }}>
      <div className="grid gap-2 md:grid-cols-[1fr_16rem_18rem] md:items-center">
        <div className="min-w-0 text-sm">
          {node.level > 0 && <CornerDownRight className="mr-1 inline size-3 text-slate-400" />}
          <AssetLink asset={node} /> {node.name}
          <div className="truncate text-xs text-slate-500">
            {node.asset_type.label}
            {node.serial_number ? ` · SN ${node.serial_number}` : ''}
          </div>
        </div>
        <Select aria-label={`Entscheidung für ${node.asset_number}`} value={decision.action} onChange={(e) => onChange({ action: e.target.value as Decision['action'] })}>
          <option value="">– bitte entscheiden –</option>
          {Object.entries(CHILD_ACTION_LABELS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </Select>
        <div>
          {(decision.action === 'remove_to_stock' || decision.action === 'keep_as_independent_asset') && (
            <LocationSelect
              value={decision.target_location}
              onChange={(v) => onChange({ target_location: v })}
              emptyLabel={decision.action === 'remove_to_stock' ? '– Standard-Ziel –' : '– Standort des Geräts –'}
            />
          )}
          {decision.action === 'move_to_other_asset' && (
            <AssetPicker value={decision.target_asset} onChange={(a) => onChange({ target_asset: a })} exclude={exclude} placeholder="Zielgerät …" />
          )}
        </div>
      </div>
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}
